import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material";
import { INPUT_COLOR_BORDER } from "../../../utils/colors";

const AnnotationMentionList = forwardRef(
  ({ items = [], command = () => {} }, ref) => {
    const theme = useTheme();
    const [selectedIndex, setSelectedIndex] = useState(0);

    useEffect(() => {
      setSelectedIndex(0);
    }, [items]);

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }) => {
        if (event.key === "ArrowUp") {
          setSelectedIndex((prev) => (prev <= 0 ? items.length - 1 : prev - 1));
          return true;
        }

        if (event.key === "ArrowDown") {
          setSelectedIndex((prev) => (prev >= items.length - 1 ? 0 : prev + 1));
          return true;
        }

        if (event.key === "Enter") {
          const item = items[selectedIndex];
          if (item) {
            command(item);
          }
          return true;
        }

        return false;
      },
    }));

    if (!items.length) {
      return null;
    }

    const highlightSx = {
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
      fontWeight: 600,
    };

    return (
      <Box
        sx={{
          backgroundColor: "background.paper",
          border: `1px solid ${INPUT_COLOR_BORDER}`,
          borderRadius: 1,
          boxShadow: 4,
          maxHeight: 220,
          overflowY: "auto",
          py: 0.5,
          minWidth: 180,
          color: theme.palette.text.primary,
        }}
      >
        {items.map((item, index) => {
          const isSelected = index === selectedIndex;

          return (
            <Box
              key={item.id}
              onMouseDown={(event) => {
                event.preventDefault();
                command(item);
              }}
              onMouseEnter={() => setSelectedIndex(index)}
              sx={{
                px: 1.5,
                py: 1,
                mx: 0.5,
                borderRadius: 1,
                cursor: "pointer",
                transition: "background-color 0.12s ease",
                color: theme.palette.text.primary,
                ...(isSelected ? highlightSx : {}),
                "&:hover": highlightSx,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: isSelected ? 600 : 400,
                }}
              >
                {item.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  }
);

AnnotationMentionList.displayName = "AnnotationMentionList";

export default AnnotationMentionList;
